// src/pages/employee/EmployeeLeave.jsx
// Past dates are disabled on all date inputs.
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useMyLeaveRequests,
  submitLeaveRequest,
  useAllUsers,
  useOrgSettings,
  getTodayStr,
} from "../../hooks/useFirestore";
import { EMPLOYEE_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function EmployeeLeave() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid;

  const { data: requests, loading } = useMyLeaveRequests(uid);
  const { data: users } = useAllUsers();

  const myTeam = users.filter(
    u => u.teamId === userProfile?.teamId && u.uid !== uid,
  );

  const today = getTodayStr();

  const [form, setForm] = useState({
    leaveType: "casual",
    fromDate: "",
    toDate: "",
    reason: "",
    handoverTo: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [totalDays, setTotalDays] = useState(0);

  function handleChange(e) {
  const { name, value } = e.target;

  setForm((prev) => {
    const updated = {
      ...prev,
      [name]: value,
    };

    if (updated.fromDate && updated.toDate) {
      const from = new Date(updated.fromDate);
      const to = new Date(updated.toDate);

      let days = 0;
      let current = new Date(from);

      while (current <= to) {
        if (current.getDay() !== 0) {
          days++;
        }
        current.setDate(current.getDate() + 1);
      }

      setTotalDays(days);
    } else {
      setTotalDays(0);
    }

    return updated;
  });
}
  async function handleSubmit(e) {
  e.preventDefault();
  setMsg("");

  // Client-side past-date guard
  if (form.fromDate < today) {
    setMsg("❌ Leave start date cannot be in the past.");
    return;
  }

  if (form.toDate < form.fromDate) {
    setMsg("❌ End date must be on or after the start date.");
    return;
  }

  // 🧮 Calculate requested leave days (excluding Sundays)
  const from = new Date(form.fromDate);
  const to = new Date(form.toDate);

  let totalDays = 0;
  let curr = new Date(from);
  while (curr <= to) {
    if (curr.getDay() !== 0) {
      totalDays++;
    }
    curr.setDate(curr.getDate() + 1);
  }

  // ✅ Current leave balance
  const currentBalance = bal[form.leaveType] || 0;

  // ✅ Prevent negative leave balance
  if (totalDays > currentBalance) {
    setMsg(
      `❌ Leave not applicable. Only ${currentBalance} ${form.leaveType} leave(s) remaining.`
    );
    return;
  }

  setSaving(true);

  try {
    const handoverUser = myTeam.find(
      u => u.uid === form.handoverTo
    );

    await submitLeaveRequest(userProfile, {
      ...form,
      totalDays,
      handoverToName:
        handoverUser?.displayName || "",
    });

    setMsg("✅ Leave request submitted!");

    setForm({
      leaveType: "casual",
      fromDate: "",
      toDate: "",
      reason: "",
      handoverTo: "",
    });

  } catch (err) {
    setMsg("❌ " + err.message);
  }

  setSaving(false);
}

  const { data: orgSettings } = useOrgSettings();
  const configuredPolicy = orgSettings?.leavePolicy || { casual: 12, sick: 12, annual: 21 };
  const ent = userProfile?.leaveEntitlement || configuredPolicy;
  const bal = userProfile?.leaveBalance || ent;

  return (
    <Layout navItems={EMPLOYEE_NAV} panelLabel="My Portal">
      <div className="page-header">
        <h1 className="page-title">My Leaves</h1>
      </div>

      {/* Leave balance cards */}
      <div className="leave-balance-row">
        {[
          { type: "casual", label: "Casual Leave",  color: "blue"  },
          { type: "sick",   label: "Sick Leave",    color: "green" },
          { type: "annual", label: "Annual Leave",  color: "amber" },
        ].map(({ type, label, color }) => (
          <div key={type} className={`leave-balance-card leave-balance-card--${color}`}>
            <span className="lbc-label">{label}</span>
            <span className="lbc-balance">
  {Math.max(0, bal[type] ?? 0)}
</span>
            <span className="lbc-sub">
              {(ent[type] || 0) - (bal[type] || 0)} used of {ent[type] || 0}
            </span>
            <div className="lbc-bar">
              <div
                className="lbc-fill"
                style={{
                  width: ent[type] > 0
                    ? `${Math.round(((ent[type] - bal[type]) / ent[type]) * 100)}%`
                    : "0%",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="two-col-layout">
        {/* Apply form */}
        <div className="form-card">
          <h3 className="form-card-title">Apply for Leave</h3>
          {msg && (
            <div className={msg.startsWith("✅") ? "form-success" : "form-error"}>
              {msg}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Leave Type *</label>
              <select name="leaveType" value={form.leaveType} onChange={handleChange}>
                <option value="casual">Casual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="annual">Annual Leave</option>
              </select>
            </div>
            <div className="form-row">
  <div className="form-group">
    <label>From *</label>
    <input
      name="fromDate"
      type="date"
      value={form.fromDate}
      onChange={handleChange}
      required
      min={today}
    />
  </div>

  <div className="form-group">
    <label>To *</label>
    <input
      name="toDate"
      type="date"
      value={form.toDate}
      onChange={handleChange}
      required
      min={form.fromDate || today}
    />
  </div>

  <div className="form-group">
    <label>Days</label>
    <input
      type="text"
      value={totalDays > 0 ? `${totalDays} Day${totalDays > 1 ? "s" : ""}` : ""}
      readOnly
    />
  </div>
</div>
            <div className="form-group">
              <label>Reason *</label>
              <textarea
                name="reason"
                value={form.reason}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Reason for leave…"
              />
            </div>
            <div className="form-group">
              <label>Handover To</label>
              <select name="handoverTo" value={form.handoverTo} onChange={handleChange}>
                <option value="">— Select colleague —</option>
                {myTeam.map(u => (
                  <option key={u.uid} value={u.uid}>{u.displayName}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        </div>

        {/* Leave history */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Leave History</h3>
          </div>
          {loading ? (
            <div className="card-loading">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="card-empty">No leave requests yet.</div>
          ) : (
            <div className="leave-history-list">
              {requests.map(r => (
                <div key={r.id} className="leave-history-item">
                  <div className="leave-history-header">
                    <span className="leave-history-type" style={{ textTransform: "capitalize" }}>
                      {r.leaveType} Leave
                    </span>
                    <span className={`status-pill status-pill--${
                      r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "amber"
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  <div
  className="leave-history-dates"
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="15"
    height="15"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
    />
  </svg>

  <span>
    {r.fromDate} → {r.toDate} · {r.totalDays} day
    {r.totalDays > 1 ? "s" : ""}
  </span>
</div>
                  <div className="leave-history-reason">{r.reason}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}